'use client';

import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { aspectRatioValue, getVideo } from '@/lib/media/video-catalog';
import styles from './FacadeVideo.module.css';

/**
 * FacadeVideo — a reusable, performance-first video player.
 *
 * Renders only a poster image + an accessible play button up front (the
 * "facade"). The real `<video>` element — and any media bytes — mount ONLY
 * after the user clicks, so the page ships zero video weight and no
 * third-party JS until intent. The poster sits in a fixed aspect-ratio frame
 * (zero CLS) and is lazy by default so it never competes with the page's LCP.
 *
 * Drive it from the typed catalog: `<FacadeVideo videoId="homepage-explainer" />`.
 */
export interface FacadeVideoProps {
  /** Catalog id (see `@/lib/media/video-catalog`). */
  videoId: string;
  /** Render a collapsible on-page transcript below the player (AEO + a11y). */
  showTranscript?: boolean;
  /** Mark the poster high-priority. Default false so the page's hero keeps LCP. */
  posterPriority?: boolean;
  className?: string;
}

export function FacadeVideo({
  videoId,
  showTranscript = false,
  posterPriority = false,
  className,
}: FacadeVideoProps) {
  const entry = getVideo(videoId);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Pause when scrolled out of view — frees CPU/data and stops audio playing
  // off-screen. Pure polish; the player is fully functional without it.
  useEffect(() => {
    if (!playing) return;
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([e]) => {
        if (e && !e.isIntersecting && !el.paused) el.pause();
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [playing]);

  if (!entry) return null;

  // Reserve the exact frame up front → no layout shift when the video mounts.
  const frameStyle: CSSProperties = { aspectRatio: String(aspectRatioValue(entry.aspect)) };

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className={styles.frame} style={frameStyle}>
        {playing ? (
          <video
            ref={videoRef}
            className={styles.video}
            controls
            autoPlay
            playsInline
            preload="none"
            poster={entry.poster}
            // Cross-origin captions (<track> from media.bizrnr.com) only load when
            // the media element is in CORS mode AND the CDN sends ACAO. The R2
            // bucket has CORS (ACAO:*); without this attribute the track fails
            // with a CORS "origin" error and shows no cues.
            crossOrigin="anonymous"
          >
            {entry.sources.map((s) => (
              <source key={s.src} src={s.src} type={s.type} />
            ))}
            {entry.captionsVtt ? (
              <track kind="captions" srcLang="en" label="English" src={entry.captionsVtt} default />
            ) : null}
          </video>
        ) : (
          <button
            type="button"
            className={styles.facadeButton}
            onClick={() => setPlaying(true)}
            aria-label={`Play video: ${entry.title}`}
          >
            <Image
              className={styles.poster}
              src={entry.poster}
              alt=""
              aria-hidden="true"
              fill
              sizes="(max-width: 900px) 100vw, 900px"
              priority={posterPriority}
              loading={posterPriority ? undefined : 'lazy'}
            />
            <span aria-hidden="true" className={styles.scrim} />
            <span aria-hidden="true" className={styles.playBadge}>
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" focusable="false">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>

      {showTranscript && entry.transcript && entry.transcript.length > 0 ? (
        <details className={styles.transcript}>
          <summary className={styles.transcriptSummary}>Read the transcript</summary>
          <div className={styles.transcriptBody}>
            {entry.transcript.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

export default FacadeVideo;
